// The fake api's organisations and venues (spec/14 §3.1, §3.3), past the HQ guard in
// `fake-server.ts`: HQ's record of every organisation and whether its owner has joined, inviting a café
// with its owner's invitation, following that invitation — a fresh one, withdrawing one, its link
// (ADR-0046) — every organisation's venues with the api's filters, and the listing flags. Bodies and
// queries are checked with the contract's own schemas, so a field the screen gets wrong is refused in the
// server's words, at the path the server names.

import type { CreatedInvitation, HqOrganization, HqOrganizationListItem, HqOrganizationOwner, HqOwnerInvitation, HqVenue, Problem, Venue } from '@clavian/api-client';
import {
  CreateHqOrganizationSchema,
  HqOrganizationListQuerySchema,
  HqVenueQuerySchema,
  OrganizationDecisionSchema,
  OwnerInvitationContactSchema,
  parseVenueStatusList,
  UpdateVenueListingSchema,
  type z,
} from '@clavian/contracts';
import { organizationSlugFrom } from '@clavian/domain/organization-slug';

import type { FakeResponse } from './fake-server';

const NOW = '2026-09-22T09:00:00.000Z';
const EXPIRES = '2026-09-29T09:00:00.000Z';

/** The second organisation the fake knows: a franchisee with no PAN recorded and no owner yet. */
export const NITRO = { id: 'org_01k5znw4hmfay8vvr14d2pf2de', slug: 'nitro-arcade', name: 'Nitro Arcade' };
export const NITRO_VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2de';

export interface DirectoryHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
}

/** An owner invitation as the fake keeps it: its contact in full, and what became of it. */
export interface StoredOwnerInvitation {
  id: string;
  contact: { email?: string; phoneNumber?: string };
  status: 'pending' | 'accepted' | 'revoked';
  invitedAt: string;
  invitedByName: string | null;
  expiresAt: string;
  revokedAt: string | null;
  delivery: HqOwnerInvitation['delivery'];
}

interface StoredOrganization {
  record: HqOrganization;
  /** Whether an owner has accepted: HQ may invite one only while none has (spec/14 §3.1). */
  hasOwner: boolean;
  /** People already on its staff, by email or phone number. */
  staff: string[];
  /** The owner who joined, when one has. */
  owner?: { name: string | null; email: string | null; phoneNumber: string | null; joinedAt: string };
  /** Its owner invitations, oldest first. */
  invitations?: StoredOwnerInvitation[];
}

/** HQ's own name, as the fake's HQ member is called. */
const HQ_MEMBER_NAME = 'Asha Rao';

const invalid = (helpers: DirectoryHelpers, error: z.ZodError) =>
  helpers.problem(400, 'validation_failed', 'Validation failed', {
    detail: 'The request could not be understood as written.',
    errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
  });

/** A masked destination, as `access` masks one: enough to recognise, not to reuse. */
function mask(contact: { email?: string; phoneNumber?: string }): string {
  if (contact.email !== undefined) {
    const [local = '', domain = ''] = contact.email.split('@');
    return `${local.slice(0, 1)}***@${domain}`;
  }
  const phone = contact.phoneNumber ?? '';
  return `${phone.slice(0, 3)}******${phone.slice(-2)}`;
}

export function createFakeHqDirectory(options: { readonly organizationId: string; readonly organizationName: string; readonly venues: readonly Venue[] }) {
  const state = {
    organizations: [
      // Newest first, as the api lists them.
      {
        // A pending applicant awaiting the Provider's decision (ADR-0019 §6): no owner, no venues yet.
        record: {
          id: 'org_01k5znw4hmfay8vvr14d2pf9p1',
          name: 'Skylab Gaming',
          slug: 'skylab',
          legalName: 'Skylab Gaming Private Limited',
          kind: 'independent',
          franchisorOrganizationId: null,
          pan: null,
          registeredAddress: { line1: '9 Residency Road', locality: 'Bengaluru', region: 'Karnataka', postalCode: '560025', country: 'IN' },
          hqAccess: 'full',
          status: 'pending',
          currency: 'INR',
          createdAt: '2026-09-27T10:00:00.000Z',
        },
        hasOwner: false,
        staff: [],
        invitations: [],
      },
      {
        record: {
          id: NITRO.id,
          name: NITRO.name,
          slug: NITRO.slug,
          legalName: 'Nitro Arcade LLP',
          kind: 'franchisee',
          franchisorOrganizationId: null,
          pan: null,
          registeredAddress: { line1: '4 FC Road', locality: 'Pune', region: 'Maharashtra', postalCode: '411004', country: 'IN' },
          hqAccess: 'full',
          status: 'active',
          currency: 'INR',
          createdAt: '2026-09-21T10:30:00.000Z',
        },
        hasOwner: false,
        staff: [],
        // Its owner was invited and never accepted: the link has expired.
        invitations: [
          {
            id: 'inv_01k5znw4hmfay8vvr14d2pf2de',
            contact: { email: 'neha@nitro.example' },
            status: 'pending',
            invitedAt: '2026-09-10T10:30:00.000Z',
            invitedByName: HQ_MEMBER_NAME,
            expiresAt: '2026-09-17T10:30:00.000Z',
            revokedAt: null,
            delivery: { status: 'sent', channel: 'email', at: '2026-09-10T10:30:01.000Z' },
          },
        ],
      },
      {
        record: {
          id: options.organizationId,
          name: options.organizationName,
          slug: 'playground',
          legalName: 'Playground Gaming Private Limited',
          kind: 'independent',
          franchisorOrganizationId: null,
          pan: 'AAACP1234Q',
          registeredAddress: {
            line1: '12 MG Road',
            landmark: 'Opposite the metro station',
            locality: 'Bengaluru',
            region: 'Karnataka',
            postalCode: '560001',
            country: 'IN',
          },
          hqAccess: 'full',
          status: 'active',
          currency: 'INR',
          createdAt: '2026-09-01T08:00:00.000Z',
        },
        hasOwner: true,
        staff: ['riya@playground.example'],
        owner: { name: 'Riya Sharma', email: 'r•••@playground.example', phoneNumber: '•••210', joinedAt: '2026-09-01T09:00:00.000Z' },
        invitations: [],
      },
    ] as StoredOrganization[],
    /** Oldest first, as the api lists them. */
    venues: [...options.venues] as Venue[],
    created: 0,
    invitations: 0,
  };

  const byId = (id: string) => state.organizations.find((organization) => organization.record.id === id);

  /** A new owner invitation: every pending one withdrawn first, so only the newest link works. */
  function invitation(organizationId: string, contact: { email?: string; phoneNumber?: string }): CreatedInvitation {
    state.invitations += 1;
    const id = `inv_01k5znw4hmfay8vvr14d2pf${String(300 + state.invitations)}`;
    const email = contact.email !== undefined;
    const organization = byId(organizationId);
    if (organization !== undefined) {
      organization.invitations ??= [];
      for (const earlier of organization.invitations) {
        if (earlier.status === 'pending') {
          earlier.status = 'revoked';
          earlier.revokedAt = NOW;
        }
      }
      organization.invitations.push({
        id,
        contact,
        status: 'pending',
        invitedAt: NOW,
        invitedByName: HQ_MEMBER_NAME,
        expiresAt: EXPIRES,
        revokedAt: null,
        delivery: email ? { status: 'sent', channel: 'email', at: NOW } : { status: 'not_sent', channel: null, at: NOW },
      });
    }
    return {
      id,
      organizationId,
      channel: email ? 'email' : 'phone',
      destination: mask(contact),
      role: 'owner',
      venueIds: [],
      status: 'pending',
      expiresAt: EXPIRES,
      createdAt: NOW,
      acceptUrl: `https://app.clavian.test/invitations/accept#${id}-token`,
      // An email goes out; a phone invitation waits for SMS and WhatsApp (F-CMP-09).
      delivery: email ? { status: 'sent', channel: 'email' } : { status: 'not_sent' },
    };
  }

  /** What became of a stored invitation, by the fake's clock. */
  const statusOf = (stored: StoredOwnerInvitation): HqOwnerInvitation['status'] =>
    stored.status === 'pending' && stored.expiresAt <= NOW ? 'expired' : stored.status;

  function ownerInvitationView(stored: StoredOwnerInvitation): HqOwnerInvitation {
    return {
      id: stored.id,
      channel: stored.contact.email !== undefined ? 'email' : 'phone',
      destination: mask(stored.contact),
      status: statusOf(stored),
      invitedAt: stored.invitedAt,
      invitedByName: stored.invitedByName,
      expiresAt: stored.expiresAt,
      delivery: stored.delivery,
      acceptedAt: null,
      revokedAt: stored.revokedAt,
    };
  }

  /** Whether the owner joined, else what became of the newest invitation — the api's `HqOrganizationOwner`. */
  function ownerOf(organization: StoredOrganization): HqOrganizationOwner {
    const newest = organization.invitations?.at(-1);
    const invitationView = newest === undefined ? null : ownerInvitationView(newest);
    if (organization.hasOwner) {
      // As the api: once an owner has joined, the café's owner invitations are its own, and HQ reads none.
      return {
        status: 'joined',
        invitation: null,
        owner: organization.owner ?? { name: null, email: null, phoneNumber: null, joinedAt: organization.record.createdAt },
      };
    }
    const status: HqOrganizationOwner['status'] =
      invitationView === null
        ? 'none'
        : invitationView.status === 'pending'
          ? 'invited'
          : invitationView.status === 'expired'
            ? 'expired'
            : invitationView.status === 'revoked'
              ? 'withdrawn'
              : 'none';
    return { status, invitation: invitationView, owner: null };
  }

  function listItem(organization: StoredOrganization): HqOrganizationListItem {
    const owner = ownerOf(organization);
    const at =
      owner.status === 'joined'
        ? (owner.owner?.joinedAt ?? null)
        : owner.status === 'invited'
          ? (owner.invitation?.invitedAt ?? null)
          : owner.status === 'expired'
            ? (owner.invitation?.expiresAt ?? null)
            : owner.status === 'withdrawn'
              ? (owner.invitation?.revokedAt ?? null)
              : null;
    return { ...organization.record, owner: { status: owner.status, at } };
  }

  function organizationOf(venue: Venue) {
    const organization = byId(venue.organizationId);
    return { id: venue.organizationId, name: organization?.record.name ?? venue.organizationId };
  }

  /** `undefined` for a path that is not the directory's. */
  function handle(helpers: DirectoryHelpers, method: string, pathname: string, searchParams: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const { json, problem } = helpers;

    if (pathname === '/v1/hq/organizations') {
      if (method === 'GET') {
        const query = HqOrganizationListQuerySchema.safeParse(Object.fromEntries(searchParams));
        if (!query.success) return invalid(helpers, query.error);
        const limit = query.data.limit;
        const start = Number(query.data.cursor ?? 0);
        const shown = query.data.owner === 'not_joined' ? state.organizations.filter((organization) => !organization.hasOwner) : state.organizations;
        const page = shown.slice(start, start + limit).map(listItem);
        const next = start + limit < shown.length ? String(start + limit) : null;
        return json(200, { data: page, page: { cursor: next, hasMore: next !== null, count: page.length } });
      }
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = CreateHqOrganizationSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      const body = parsed.data;
      // As the api: the slug given, or the one the name makes — taken is 409, never a suffixed second one.
      const slug = body.slug ?? organizationSlugFrom(body.name);
      if (slug.length < 3) {
        return problem(400, 'validation_failed', 'Validation failed', {
          detail: 'The name makes no slug. Give one.',
          errors: [{ field: 'slug', code: 'required', message: 'the name does not make a slug; give one' }],
        });
      }
      if (state.organizations.some((organization) => organization.record.slug === slug)) {
        return problem(409, 'slug_taken', 'That slug is taken', { detail: 'Another organisation already has that slug.' });
      }
      state.created += 1;
      const record: HqOrganization = {
        id: `org_01k5znw4hmfay8vvr14d2pf${String(300 + state.created)}`,
        name: body.name,
        slug,
        legalName: body.legalName ?? null,
        kind: body.kind ?? 'independent',
        franchisorOrganizationId: body.franchisorOrganizationId ?? null,
        pan: body.pan ?? null,
        registeredAddress: body.registeredAddress ?? null,
        hqAccess: body.hqAccess ?? 'full',
        status: 'active',
        currency: 'INR',
        createdAt: NOW,
      };
      state.organizations.unshift({ record, hasOwner: false, staff: [], invitations: [] });
      return json(201, { organization: record, ownerInvitation: invitation(record.id, body.owner) });
    }

    // Whether the owner joined, and the newest owner invitation (ADR-0046).
    const ownerRead = /^\/v1\/hq\/organizations\/([^/]+)\/owner$/.exec(pathname);
    if (ownerRead !== null) {
      if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
      const organization = byId(ownerRead[1] ?? '');
      return organization === undefined ? problem(404, 'not_found', 'No such organisation.') : json(200, ownerOf(organization));
    }

    // One owner invitation: a fresh one to its contact, its link, or withdrawing it (ADR-0046).
    const ownerInvitation = /^\/v1\/hq\/organizations\/([^/]+)\/owner-invitations\/([^/]+?)(\/resend|\/link)?$/.exec(pathname);
    if (ownerInvitation !== null) {
      const organization = byId(ownerInvitation[1] ?? '');
      const stored = organization?.invitations?.find((candidate) => candidate.id === ownerInvitation[2]);
      if (organization === undefined || stored === undefined) return problem(404, 'not_found', 'No such owner invitation.');
      const action = ownerInvitation[3];
      // As the api: once an owner has joined, HQ neither resends, withdraws nor hands out an owner invitation.
      if (organization.hasOwner) return problem(409, 'conflict', 'Conflict', { detail: 'This organisation has an owner.' });
      if (action === '/resend' && method === 'POST') {
        if (stored.status === 'accepted') return problem(409, 'invitation_unavailable', 'Invitation used, revoked or expired');
        return json(201, invitation(organization.record.id, stored.contact));
      }
      if (action === '/link' && method === 'POST') {
        if (statusOf(stored) !== 'pending') return problem(409, 'invitation_unavailable', 'Invitation used, revoked or expired');
        return json(200, { acceptUrl: `https://app.clavian.test/invitations/accept#${stored.id}-token`, expiresAt: stored.expiresAt });
      }
      if (action === undefined && method === 'DELETE') {
        if (statusOf(stored) !== 'pending') return problem(409, 'invitation_unavailable', 'Invitation used, revoked or expired');
        stored.status = 'revoked';
        stored.revokedAt = NOW;
        return json(204, undefined);
      }
      return problem(405, 'method_not_allowed', 'Method not allowed');
    }

    const one = /^\/v1\/hq\/organizations\/([^/]+)$/.exec(pathname);
    if (one !== null) {
      if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
      const organization = byId(one[1] ?? '');
      return organization === undefined ? problem(404, 'not_found', 'No such organisation.') : json(200, organization.record);
    }

    const owner = /^\/v1\/hq\/organizations\/([^/]+)\/owner-invitations$/.exec(pathname);
    if (owner !== null && method === 'POST') {
      const organization = byId(owner[1] ?? '');
      if (organization === undefined) return problem(404, 'not_found', 'No such organisation.');
      const parsed = OwnerInvitationContactSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      if (organization.hasOwner) {
        return problem(409, 'conflict', 'Conflict', {
          detail: 'This organisation has an owner. Its owners invite owners; HQ invites one only while there is none.',
        });
      }
      const contact = parsed.data.email ?? parsed.data.phoneNumber ?? '';
      if (organization.staff.includes(contact)) return problem(409, 'already_a_member', 'Already a member');
      return json(201, invitation(organization.record.id, parsed.data));
    }

    // The Provider's decision on a pending organisation (ADR-0019 §6): approve → active, decline → suspended.
    const approval = /^\/v1\/hq\/organizations\/([^/]+)\/approval$/.exec(pathname);
    if (approval !== null && method === 'POST') {
      const organization = byId(approval[1] ?? '');
      if (organization === undefined) return problem(404, 'not_found', 'No such organisation.');
      const parsed = OrganizationDecisionSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      if (organization.record.status !== 'pending') return problem(409, 'conflict', 'The organisation is not awaiting approval.');
      organization.record = { ...organization.record, status: parsed.data.decision === 'approve' ? 'active' : 'suspended' };
      return json(200, organization.record);
    }

    if (pathname === '/v1/hq/venues' && method === 'GET') {
      const parsed = HqVenueQuerySchema.safeParse(Object.fromEntries(searchParams));
      if (!parsed.success) return invalid(helpers, parsed.error);
      const query = parsed.data;
      const statuses = parseVenueStatusList(query.status);
      const flag = (value: 'true' | 'false' | undefined, actual: boolean) => value === undefined || (value === 'true') === actual;
      const matching = state.venues.filter(
        (venue) =>
          (query.organizationId === undefined || venue.organizationId === query.organizationId) &&
          (query.q === undefined || `${venue.name} ${venue.slug}`.toLowerCase().includes(query.q.toLowerCase())) &&
          (query.city === undefined || venue.address?.locality.toLowerCase() === query.city.toLowerCase()) &&
          (statuses.length === 0 || statuses.includes(venue.status)) &&
          flag(query.publicListing, venue.publicListing) &&
          flag(query.featured, venue.featured) &&
          flag(query.partner, venue.partner) &&
          flag(query.sponsored, venue.sponsored),
      );
      const start = Number(query.cursor ?? 0);
      const page: HqVenue[] = matching.slice(start, start + query.limit).map((venue) => ({ venue, organization: organizationOf(venue) }));
      const next = start + query.limit < matching.length ? String(start + query.limit) : null;
      return json(200, { data: page, page: { cursor: next, hasMore: next !== null, count: page.length } });
    }

    const listing = /^\/v1\/hq\/organizations\/([^/]+)\/venues\/([^/]+)\/listing$/.exec(pathname);
    if (listing !== null && method === 'PATCH') {
      const index = state.venues.findIndex((venue) => venue.organizationId === listing[1] && venue.id === listing[2]);
      if (index === -1) return problem(404, 'not_found', 'No such venue in that organisation.');
      const parsed = UpdateVenueListingSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      const venue = { ...(state.venues[index] as Venue), ...parsed.data, updatedAt: NOW };
      state.venues[index] = venue;
      return json(200, venue, { etag: `"venue-hq-${index}-${NOW}"` });
    }

    return undefined;
  }

  return { state, handle };
}

export type FakeHqDirectory = ReturnType<typeof createFakeHqDirectory>;
